import * as React from 'react';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/hooks/useAuth';
import { api, getErrorMessage } from '@/api/api';
import { useEchoStore } from '@/context/EchoContext';

interface UseDeleteCreatorProfileReturn {
  deleteCreatorProfile: () => Promise<void>;
  isDeleting: boolean;
}

/**
 * Returns a `deleteCreatorProfile()` that wipes the user's creator profile via the API,
 * clears the echo store, surfaces a toast on success/failure, and exposes `isDeleting`
 * for spinners. Does NOT delete the user account.
 */
export function useDeleteCreatorProfile(): UseDeleteCreatorProfileReturn {
  const { user, updateUser } = useAuth();
  const { clearEchoData } = useEchoStore();
  const { addToast } = useToast();
  const [isDeleting, setIsDeleting] = React.useState(false);

  const deleteCreatorProfile = React.useCallback(async () => {
    setIsDeleting(true);
    try {
      const response = await api.users.deleteCreatorProfile();

      if (user) {
        updateUser({ ...user, role: 'fan' });
      }

      clearEchoData();

      addToast({
        title: 'Profile Deleted',
        subtitle: response.message || 'Your creator profile has been deactivated.',
        type: 'success',
      });
    } catch (error) {
      addToast({
        title: 'Error',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [user, updateUser, clearEchoData, addToast]);

  return { deleteCreatorProfile, isDeleting };
}
